import { ApolloClient, ApolloProvider, useSubscription } from "@apollo/client";
import { InMemoryCache } from "apollo-cache-inmemory";
import { ApolloLink, split } from "apollo-link";
import { onError } from "apollo-link-error";
import { HttpLink } from "apollo-link-http";
import { WebSocketLink } from "apollo-link-ws";
import { getMainDefinition } from "apollo-utilities";
import gql from "graphql-tag";
import Cookies from "js-cookie";
import React, { Suspense } from "react";
import { Redirect, Route, Router, Switch } from "react-router-dom";
import { cssTransition, toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";
import browserHistory from "./browserHistory";
import { Admin } from "./Components/Admin/Admin";
import { Login } from "./Components/Login/Login";
import { Logout } from "./Components/Logout/Logout";
import { ShortCode } from "./Components/ShortCode/ShortCode";
import { User } from "./Components/User/User";
import { CurrentLanguageContextProvider } from "./Context/CurrentLanguageContext/CurrentLanguageContext";
import { DistributorSearchProvider } from "./Context/DistributorSearchContext/DistributorSearchContext";
import { errorHandler } from "./GraphQL/errorHandler";
import { useQueryString } from "./Hooks/useQueryString";

// Loading component
// Added as a quick fix to i18Next when implementing http backend, ie
// the asynchronous loading of translation files, which suspends the render
// TODO: Make a visually nice version of this
const SuspenseFallback = () => {
  return <div></div>;
};

export const App = () => {
  return (
    <ApolloProvider client={client}>
      <Suspense fallback={<SuspenseFallback />}>
        <CurrentLanguageContextProvider>
          <DistributorSearchProvider>
            <Router history={browserHistory}>
              <KeepAliveComponent />
              <div className="App">
                <Switch>
                  <Route path="/login" component={Login} />
                  <Route path="/admin" component={Admin} />
                  <Route path="/sso" component={User} />
                  <Route path="/logout" component={Logout} />
                  <Route path="/:shortCode" component={ShortCode} />
                  <Route render={() => <AuthenticatedComponent />} />
                </Switch>
                <div id="modalroot" />
                <ToastContainer
                  position={toast.POSITION.BOTTOM_RIGHT}
                  autoClose={8000}
                />
              </div>
            </Router>
          </DistributorSearchProvider>
        </CurrentLanguageContextProvider>
      </Suspense>
    </ApolloProvider>
  );
};

const AuthenticatedComponent = () => {
  const queryParams = useQueryString();
  const accessLevel = Cookies.get("access_level");
  const redirectParam = queryParams.get("redirect");

  if (redirectParam) {
    return <Redirect to={`/login?redirect=${redirectParam}`} />;
  } else {
    switch (accessLevel) {
      case "SUPERADMIN":
        return <Redirect to="/admin" />;
      case "DISTRIBUTOR":
        return <Redirect to="/admin" />;
      case "CUSTOMER":
        return <Redirect to="/sso" />;
      default:
        return <Redirect to="/login" />;
    }
  }
};

const KeepAliveComponent = () => {
  useSubscription(keepAliveSubscription);
  return <div />;
};

export const apps = [
  {
    name: "EFLOW",
    icon: "iconEflow",
    title: "eflow — booking & workflow",
    url: process.env.REACT_APP_WORKFLOW_URL,
  },
  {
    name: "INVENTORY",
    icon: "iconLagerstyring",
    title: "Lagerstyring",
    url: process.env.REACT_APP_INVENTORY_URL,
  },
  {
    name: "SHOP",
    icon: "iconWebshop",
    title: "Webshop",
    url: process.env.REACT_APP_WEBSHOP_URL,
  },
];

const httpLink = new HttpLink({
  uri: process.env.REACT_APP_API,
  credentials: "include",
  fetch,
});

const wsLink = new WebSocketLink({
  uri:
    process.env.NODE_ENV === "production"
      ? `wss://${window.location.host}${process.env.REACT_APP_SOCKET}`
      : process.env.REACT_APP_SOCKET,
  options: {
    reconnect: true,
  },
  credentials: "include",
});

const link = split(
  // split based on operation type
  ({ query }) => {
    const { kind, operation } = getMainDefinition(query);
    return kind === "OperationDefinition" && operation === "subscription";
  },
  wsLink,
  httpLink
);

const errorLink = onError(({ graphQLErrors }) => {
  if (graphQLErrors) {
    graphQLErrors.map((err) => {
      return errorHandler(err);
    });
  }
});

const cache = new InMemoryCache();

export const client = new ApolloClient({
  connectToDevTools: true,
  cache,
  link: ApolloLink.from([errorLink, link]),
});

const keepAliveSubscription = gql`
  subscription keepAlive {
    keepAlivePing
  }
`;

export const Zoom = cssTransition({
  enter: "zoomIn",
  exit: "zoomOut",
});

export const toastSuccessOptions = {
  className: "SSOToast__success",
  bodyClassName: "SSOToast__success-body",
  hideProgressBar: true,
  pauseOnFocusLoss: false,
  pauseOnHover: true,
  autoClose: 4000,
  transition: Zoom,
};

export const toastErrorOptions = {
  className: "SSOToast__error",
  bodyClassName: "SSOToast__error-body",
  hideProgressBar: true,
  pauseOnFocusLoss: false,
  pauseOnHover: true,
  autoClose: 4000,
  transition: Zoom,
};
